/*
 * One way to tell the admin what happened.
 *
 * Before this, a failed request fired a toast *and* a blocking SweetAlert whose
 * body was usually "undefined", because it read `error.response.data.message`
 * on endpoints that answer with `error` or `errors` instead. Errors now go to a
 * single toast carrying a real sentence; a modal is reserved for the cases that
 * actually need a decision from the admin.
 *
 * Usage inside any component:
 *
 *   this.$notifySuccess(this.$t("bus_created"));
 *   this.$notifyError(error, this.$t("error_creating_bus"));
 *   this.$apiError(error)            -> the message string, if you need it raw
 */

import { apiErrorMessage, apiErrorList, apiErrorFromBlob, isAuthError } from "@/utils/apiError";
import auth from "@/services/AuthService";

export default {
  install(Vue) {
    Vue.prototype.$apiError = function (error, fallback) {
      return apiErrorMessage(error, fallback);
    };

    Vue.prototype.$apiErrorList = function (error) {
      return apiErrorList(error);
    };

    /**
     * Report a failed request.
     *
     * @param {Error}  error    the axios error
     * @param {string} context  what the admin was trying to do, shown as the title
     */
    Vue.prototype.$notifyError = function (error, context) {
      //a dead session is not a form error: sign out instead of nagging
      if (isAuthError(error)) {
        this.$notify({
          title: this.$t ? this.$t("error_title") : "Error",
          text: apiErrorMessage(error),
          type: "error",
        });
        auth.logout();
        return;
      }

      console.log(error);

      this.$notify({
        title: context || (this.$t ? this.$t("error_title") : "Error"),
        text: apiErrorMessage(error),
        type: "error",
        duration: 6000,
      });
    };

    /** For requests made with responseType: "blob". */
    Vue.prototype.$notifyBlobError = function (error, context) {
      const self = this;
      apiErrorFromBlob(error).then(function (message) {
        self.$notify({
          title: context || (self.$t ? self.$t("error_title") : "Error"),
          text: message,
          type: "error",
          duration: 6000,
        });
      });
    };

    Vue.prototype.$notifySuccess = function (text, context) {
      this.$notify({
        title: context || (this.$t ? this.$t("success_title") : "Success"),
        text: text,
        type: "success",
      });
    };

    /**
     * A modal, for the few places where the admin has to make a choice.
     * Returns a promise resolving to true when they confirmed.
     */
    Vue.prototype.$confirmAction = function (options) {
      return this.$swal
        .fire({
          title: options.title,
          text: options.text,
          icon: options.icon || "warning",
          showCancelButton: true,
          cancelButtonText: options.cancelText || (this.$t ? this.$t("cancel") : "Cancel"),
          confirmButtonText: options.confirmText || (this.$t ? this.$t("ok") : "OK"),
        })
        .then((result) => result.isConfirmed === true);
    };
  },
};
